import JobTagName from '@/components/job-tag';

import NomadApiClient from '@/pages/api/api-clients/nomad-api';
import { transformPublicJob } from '@/services/transformers/public-jobs-transformer';
import { endOfDay } from '@/utils/date-utils';

type JobLocation = {
  city: string;
  state: string;
};

export const availableJobTagDynamicNames = ['nomadsWorkedHere'] as const;
export type JobTagDynamicName = (typeof availableJobTagDynamicNames)[number];

export const availableJobTagSpecialNames = [
  'aboveAvgPay',
  'new',
  'featured',
] as const;
export type JobTagSpecialName = (typeof availableJobTagSpecialNames)[number];

export const availableJobTagCommonNames = [
  'applicationSubmitted',
  'autoOffer',
  'easyApply',
  'exclusive',
  'firstToApply',
  'nearby',
  'oneClickApply',
  'topHospital',
  'topMatch',
] as const;
export type JobTagCommonName = (typeof availableJobTagCommonNames)[number];

export const availableJobTagNames = [
  ...availableJobTagCommonNames,
  ...availableJobTagDynamicNames,
  ...availableJobTagSpecialNames,
] as const;
export type JobTagName = (typeof availableJobTagNames)[number];

export type JobTagDynamicFormat = [JobTagDynamicName, string | number];

export type JobTagFormat = JobTagName | JobTagDynamicFormat;

export const isJobTagDynamicFormat = (
  tag: JobTagFormat
): tag is JobTagDynamicFormat => Array.isArray(tag);

export const isJobTagStringFormat = (
  tag: JobTagFormat
): tag is JobTagCommonName | JobTagSpecialName => !isJobTagDynamicFormat(tag);

export const checkIsDynamicTag = (
  tag: JobTagFormat
): tag is JobTagDynamicName =>
  isJobTagDynamicFormat(tag) && availableJobTagDynamicNames.includes(tag[0]);

export const checkIsSpecialTag = (
  tag: JobTagFormat
): tag is JobTagSpecialName =>
  isJobTagStringFormat(tag) &&
  (tag === 'aboveAvgPay' || tag === 'new' || tag === 'featured') &&
  availableJobTagSpecialNames.includes(tag);

export const checkIsCommonTag = (tag: JobTagFormat): tag is JobTagCommonName =>
  isJobTagStringFormat(tag) &&
  !checkIsDynamicTag(tag) &&
  !checkIsSpecialTag(tag) &&
  availableJobTagNames.includes(tag);

export type Job = {
  id: string;
  contractWeeks: number;
  coverImage?: string;
  facility: string;
  hiredNomads: number;
  location: JobLocation;
  tags: JobTagFormat[];
  title: string;
  simpleTitle: string;
  shift: string;
  startDate: string;
  weeklyPay: number;
  code: string;
  discipline: string;
  disciplineTitle: string;
  specialty: string;
};

export enum GetPublicJobTypeRequestParam {
  ALLIED_HEALTH = 'allied_health',
  TRAVEL = 'travel',
}

export type GetPublicJobsRequest = {
  locations?: string[] | string;
  discipline?: string;
  specialties?: string[] | string;
  limit?: number;
  jobTypes?: GetPublicJobTypeRequestParam;
};

export type GetPublicJobsResponse = {
  jobs: Job[];
};

const getAll = async (
  request: GetPublicJobsRequest
): Promise<GetPublicJobsResponse> => {
  try {
    const {
      data: {
        results: {
          matches: { items },
        },
      },
    } = await NomadApiClient.get('v1/jobposts/public_jobpost_search', {
      params: {
        locations: request.locations || 'all',
        discipline: request.discipline,
        specializations: request.specialties,
        limit: request.limit,
        ordering: 'highest_pay',
        exclusive: false,
        _origin: 'nomad-marketing',
        jobTypes: request.jobTypes,
      },
      expiresAt: endOfDay(new Date()),
    });

    return {
      jobs: items.slice(0, request.limit).map(transformPublicJob),
    };
  } catch (error) {
    return {
      jobs: [],
    };
  }
};

const PublicJobsService = {
  getAll,
};

export default PublicJobsService;
